/**
 * WidgetLibraryPanel — Browse and add widgets to the page.
 *
 * Shows a grid of widget tiles grouped by category with filter tabs.
 * Tap a tile to add the widget (appends to bottom of page).
 *
 * Reads from WIDGET_REGISTRY (shared package) — no network dependency.
 */

import { useState, useMemo } from 'react'
import { ArrowLeft, Search } from 'lucide-react'
import {
  WIDGET_REGISTRY,
  WIDGET_CATEGORIES,
  MAX_CONTAINER_DEPTH,
  isContainerType,
  type WidgetDefinition,
  type WidgetCategory,
} from '@zaviago/storefront-config'
import { WidgetTile } from './WidgetTile'

interface WidgetLibraryPanelProps {
  onAdd: (type: string) => void
  onBack: () => void
  /** When set, the library is adding a child to a container (scoped mode). */
  addTarget?: { parentEditorId: string; depth: number } | null
}

/** Category labels for tabs */
const CATEGORY_LABELS: Record<WidgetCategory | 'all', string> = {
  all: 'All',
  content: 'Content',
  products: 'Products',
  navigation: 'Navigation',
  layout: 'Layout',
  templates: 'Templates',
}

/** Dedup WIDGET_REGISTRY (aliases like "categories" share same object ref) */
function getUniqueWidgets(): WidgetDefinition[] {
  const seen = new Set<WidgetDefinition>()
  const widgets: WidgetDefinition[] = []
  for (const widget of Object.values(WIDGET_REGISTRY)) {
    if (!seen.has(widget)) {
      seen.add(widget)
      widgets.push(widget)
    }
  }
  return widgets
}

export function WidgetLibraryPanel({ onAdd, onBack, addTarget }: WidgetLibraryPanelProps) {
  const [activeCategory, setActiveCategory] = useState<WidgetCategory | 'all'>('all')
  const [search, setSearch] = useState('')

  const allWidgets = useMemo(() => getUniqueWidgets(), [])

  const filteredWidgets = useMemo(() => {
    let result = allWidgets

    // Hide container-frame when adding to a container at max depth - 1
    if (addTarget && addTarget.depth >= MAX_CONTAINER_DEPTH - 1) {
      result = result.filter((w) => !isContainerType(w.type))
    }

    // Filter by category
    if (activeCategory !== 'all') {
      result = result.filter((w) => w.category === activeCategory)
    }

    // Filter by search
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      result = result.filter(
        (w) =>
          w.schema.label.toLowerCase().includes(q) ||
          w.nameTh.includes(q) ||
          (w.schema.description?.toLowerCase().includes(q) ?? false),
      )
    }

    return result
  }, [allWidgets, activeCategory, search, addTarget])

  function handleAdd(type: string) {
    onAdd(type)
    onBack()
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        <button
          onClick={onBack}
          className="rounded-xs p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h2 className="text-sm font-semibold text-gray-700">
          {addTarget ? 'Add to Container Frame' : 'Add Section'}
        </h2>
      </div>

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search widgets..."
            className="w-full rounded-sm border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-xs text-gray-700 placeholder-gray-400 focus:border-sky-300 focus:outline-none focus:ring-1 focus:ring-sky-300"
          />
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 overflow-x-auto px-4 pt-3 pb-2 [scrollbar-width:none]">
        {(['all', ...WIDGET_CATEGORIES] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              activeCategory === cat
                ? 'bg-sky-100 text-sky-700'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Widget grid */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 pt-2 [scrollbar-width:none]">
        {filteredWidgets.length === 0 ? (
          <p className="py-8 text-center text-xs text-gray-400">
            No widgets found
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {filteredWidgets.map((widget) => (
              <WidgetTile
                key={widget.type}
                widget={widget}
                onAdd={handleAdd}
              />
            ))}
          </div>
        )}
        {addTarget && addTarget.depth >= MAX_CONTAINER_DEPTH - 1 && (
          <p className="mt-2 text-center text-xs text-gray-400">
            Containers cannot be nested beyond {MAX_CONTAINER_DEPTH} levels
          </p>
        )}
      </div>
    </div>
  )
}
